<template>
  <div>
    <footer class="px-5 md:px-0">
      <div v-if="reviewData.rating" class="border-b border-solid border-gray-200 md:border-gray-300 google-review">
        <div class="md:container px-5 flex justify-center">
          <AggregateRating
            :url="reviewData.url"
            :name="reviewData.name"
            :rating="reviewData.rating"
            :max-rating="reviewData.maxRating"
            :amount-reviews="reviewData.amountReviews"
            :src="reviewData.image"
          />
        </div>
      </div>

      <div class="container footer-container text-gray-500 md:text-gray-600">
        <div class="row footer-content ota-small-light">
          <div class="footer-content-faq col-md-3 col-xs-12">
            <ul>
              <template v-for="item in faqColumn">
                <li v-if="item.display" :key="item.link" class="relative" :class="item.classes">
                  <a v-if="item.link" :href="item.link"><span v-text="item.text" /></a>
                  <NuxtLink
                    v-else-if="item.nuxtLink"
                    :to="{ path: $i18n.localePath({ name: item.nuxtLink, params: { ...(item.params ? item.params : {}) } }), hash: item.hash }"
                  >
                    <span v-text="item.text" />
                  </NuxtLink>
                  <span v-else v-text="item.text" />
                  <div v-if="item.showArrow" class="absolute top-0 right-0 mr-5 h-full flex w-4 items-center">
                    <OSvg class="fill-gray-300 w-full" svg="arrow-right" />
                  </div>
                </li>
              </template>
            </ul>
          </div>
          <div class="footer-content-how-does-it-work col-md-3 col-xs-12">
            <ul>
              <template v-for="item in howDoesItWorkColumn">
                <li v-if="item.display" :key="item.link" class="relative" :class="item.classes">
                  <NuxtLink
                    v-if="item.nuxtLink"
                    :to="{ path: $i18n.localePath({ name: item.nuxtLink, params: { ...(item.params ? item.params : {}) } }), hash: item.hash }"
                  >
                    <span v-text="item.text" />
                  </NuxtLink>
                  <a v-else-if="item.link" :href="item.link">
                    <span v-text="item.text" />
                  </a>
                  <span v-else v-text="item.text" />
                  <div v-if="item.showArrow" class="absolute top-0 right-0 mr-5 h-full flex w-4 items-center">
                    <OSvg class="fill-gray-300 w-full" svg="arrow-right" />
                  </div>
                </li>
              </template>
            </ul>
          </div>
          <div class="footer-content-legal col-md-3 col-xs-12 hidden md:block">
            <ul>
              <template v-for="item in legalColumn">
                <li v-if="item.display" :key="item.link" class="relative" :class="item.classes">
                  <a v-if="item.link" :href="item.link"><span v-text="item.text" /></a>
                  <span v-else v-text="item.text" />
                  <div v-if="item.showArrow" class="absolute top-0 right-0 mr-5 h-full flex w-4 items-center">
                    <OSvg class="fill-gray-300 w-full" svg="arrow-right" />
                  </div>
                </li>
              </template>
            </ul>
          </div>
          <div class="col-md-3 col-xs-12 flex justify-center md:block mt-7 md:mt-0">
            <span class="ota-small text-gray-400 hidden md:inline"><span v-t="'Footer.FollowUs'" /></span>
            <div class="social-channels">
              <a :href="instagramLink" target="_blank" rel="noopener noreferrer" :aria-label="$t('Accessibilty.AriaLabels.GoToName', { name: 'Instagram' })">
                <OSvg class="w-5 h-5 md:w-7 md:h-7 inline mr-3 md:mr-2" svg="instagram" />
              </a>
              <a href="https://www.facebook.com/onthatass" target="_blank" rel="noopener noreferrer" :aria-label="$t('Accessibilty.AriaLabels.GoToName', { name: 'Facebook' })">
                <OSvg class="w-5 h-5 md:w-7 md:h-7 inline mr-3 md:mr-2" svg="facebook" />
              </a>
              <a href="https://www.youtube.com/channel/UCxbcx42q2FrlLQVly6XPuDw" target="_blank" rel="noopener noreferrer" :aria-label="$t('Accessibilty.AriaLabels.GoToName', { name: 'Youtube' })">
                <OSvg class="h-5 md:w-auto md:h-7 inline" svg="youtube" />
              </a>
            </div>
          </div>
        </div>
        <div class="mt-3 px-10 mb-10 md:mt-0 text-center md:px-0">
          <span class="md:hidden">
            <template v-for="item in legalColumn">
              <a v-if="item.link && item.display" :key="item.link" :href="item.link"><span v-text="item.text" /> | </a>
            </template>
          </span>
          <span class="md:text-xs">&copy; ON THAT ASS {{ year }}</span>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import i18nConfig from '@/config/i18n'
const OSvg = () => import('@/components/OSvg')
const AggregateRating = () => import('@/components/AggregateRating')

const { defaultLocale, locales } = i18nConfig

export default {
  components: {
    OSvg,
    AggregateRating
  },
  props: {
    locale: {
      type: String,
      default: defaultLocale,
      validator: (value) => {
        return locales.includes(value)
      }
    },
    faqColumn: {
      type: Array,
      default: () => []
    },
    howDoesItWorkColumn: {
      type: Array,
      default: () => []
    },
    legalColumn: {
      type: Array,
      default: () => []
    },
    reviewData: {
      type: Object,
      default: () => {
        return {
          name: null,
          rating: null,
          maxRating: null,
          amountReviews: null,
          image: null,
          url: null
        }
      },
      validator (value) {
        return Object.keys(value).every(prop => ['name', 'rating', 'maxRating', 'amountReviews', 'image', 'url'].includes(prop))
      }
    },
    year: {
      type: String,
      required: true
    }
  },
  computed: {
    instagramLink () {
      const isWomenSegment = this.$route?.name?.startsWith('lang-women')

      return isWomenSegment ? 'https://www.instagram.com/onthatass_women/' : 'https://instagram.com/onthatass_official/'
    }
  }
}
</script>

<style lang="postcss" scoped>
.google-review {
  @apply py-7;
}

.footer-container {
  @apply px-0;
}

li > a,
li > span {
  @apply block border-b border-solid border-gray-200 py-2 pl-6;

  &.last-item {
    @apply border-none mb-8;
  }
}

.social-channels {
  @apply ml-2 mt-2;
}

@screen md {
  .footer-content {
    @apply py-15;
  }

  .google-review {
    @apply py-10;
  }

  li {
    > a, > span {
      @apply border-none py-1 pl-0;

      &.last-item {
        @apply mb-0;
      }
    }

    svg {
      @apply hidden;
    }
  }

  .social-channels {
    @apply block ml-0;
  }
}
</style>
